/* ============================================================
   PRODUCT LIBRARY — rupa v3.9 (modal sebenar)
   ============================================================
   PAPARAN SAHAJA. Semua data, kunci PENDING, had trial, kiraan
   kuantiti dan tulisan ke hilir kekal di dalam
   [ProductLibraryModal.jsx] — fail ini menerima semuanya sebagai
   prop.

   ⛔ JANGAN pindahkan mana-mana logik ke sini. Modal lama masih
      hidup sebagai laluan patah balik bila suis `config/ui_v39`
      mati; dua salinan peraturan "produk PENDING tidak boleh
      dipilih" bermakna satu daripadanya akan terlepas pembetulan,
      dan kegagalannya senyap — pelanggan melampirkan produk tanpa
      data, dan Generate gagal selepas dia menunggu.

   Bentuknya diambil daripada pemilih prototaip dalam
   [PromptComposerV39.jsx] dan disahkan pada /workspace-v3.9:
   kotak 900px + troli 300px, baris padat satu baris dengan ID
   sebagai lajur depan, dan troli yang sentiasa terbuka.

   SAIZ (dipinda 2026-08-15): lebar 1210 KEKAL — cubaan menjadikannya
   penuh skrin ditolak user. Yang berubah cuma tinggi: 600px kini LANTAI,
   bukan tinggi tetap, dan kedua-dua panel berkongsi tinggi baris yang
   sama sehingga 88vh supaya hujung bawahnya rata (dahulu troli sahaja
   yang memanjang ikut bilangan kad). Butang Cancel + Add turut berpindah
   ke kaki troli, bersebelahan TOTAL CARD.
   ============================================================ */

import { useEffect } from 'react';
import {
    X, Search, Check, Lock, Info, Package, ShoppingCart, Minus, Plus,
    Tag, LayoutGrid, Rows3,
} from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, Field, Segmented } from '../design/ui';
import SusunMenuV39 from './SusunMenuV39';
import PilihV39 from './PilihV39';
import { SAIZ_MODAL, SAIZ_RADIUS, SAIZ_IKON } from '../utils/saizGambarSenarai';

/* Jenis susunan. "Product ID" ialah nombor yang BENAR-BENAR dipapar pada
   setiap baris (pilihan user 2026-08-14) — bukan `number` turutan sheet,
   yang tidak kelihatan di mana-mana dalam modal ini. */
/* Peta saiz gambar dikongsi dengan halaman Products — lihat fail itu untuk
   sebab "kecil" di sini (22px) lebih kecil daripada halaman penuh. */
const SUSUNAN_V39 = [
    { id: 'nombor', label: 'Product ID', lalai: 'asc', tip: { asc: '1 → 10', desc: '10 → 1' } },
    { id: 'masa', label: 'Time', lalai: 'desc', tip: { asc: 'Oldest first', desc: 'Newest first' } },
    { id: 'nama', label: 'Name', lalai: 'asc', tip: { asc: 'A → Z', desc: 'Z → A' } },
    { id: 'status', label: 'Status', lalai: 'desc', tip: { asc: 'Pending first', desc: 'Ready first' } },
];

/* ---------- Kotak semak ------------------------------------------------ */
function Kotak({ on, mati, onChange }) {
    return (
        <button
            type="button" role="checkbox" aria-checked={!!on} disabled={mati}
            onClick={(e) => { e.stopPropagation(); onChange?.(); }}
            style={{
                width: 16, height: 16, flex: 'none', padding: 0, borderRadius: 5,
                display: 'grid', placeItems: 'center',
                cursor: mati ? 'not-allowed' : 'pointer', opacity: mati ? .35 : 1,
                border: `1px solid ${on ? 'var(--s-select)' : 'var(--s-line-strong)'}`,
                background: on ? 'var(--s-select)' : 'transparent',
            }}
        >
            {on && <Check size={11} strokeWidth={3} style={{ color: 'var(--s-select-ink)' }} />}
        </button>
    );
}

/* ---------- Pelaras kuantiti ------------------------------------------- */
function Pelaras({ nilai, onUbah }) {
    const btn = (mati) => ({
        width: 20, height: 20, flex: 'none', padding: 0, borderRadius: 999,
        display: 'grid', placeItems: 'center',
        cursor: mati ? 'not-allowed' : 'pointer', opacity: mati ? .3 : 1,
        background: 'var(--s-fill)', border: '1px solid var(--s-line)', color: 'var(--s-ink-2)',
    });

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flex: 'none' }}>
            <button type="button" aria-label="Decrease" disabled={nilai <= 1}
                onClick={() => onUbah(-1)} style={btn(nilai <= 1)}>
                <Minus size={11} strokeWidth={2.4} />
            </button>
            <span style={{
                minWidth: 14, textAlign: 'center', fontSize: 12, color: 'var(--s-ink)',
                fontVariantNumeric: 'tabular-nums',
            }}>{nilai}</span>
            <button type="button" aria-label="Increase" disabled={nilai >= 20}
                onClick={() => onUbah(1)} style={btn(nilai >= 20)}>
                <Plus size={11} strokeWidth={2.4} />
            </button>
        </span>
    );
}

/* ---------- Petak gambar ------------------------------------------------
   Gambar produk SEBENAR bila ada. Ikon monokrom hanya sandaran bila
   produk itu memang tiada gambar — bukan pengganti tetap. */
function Gambar({ url, saiz, radius, ikon }) {
    return (
        <span style={{
            width: saiz, height: saiz, flex: 'none', borderRadius: radius, overflow: 'hidden',
            display: 'grid', placeItems: 'center',
            background: 'var(--s-ground)', border: '1px solid var(--s-line)', color: 'var(--s-ink-3)',
        }}>
            {url
                ? <img src={url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Package size={ikon} strokeWidth={1.5} />}
        </span>
    );
}

export default function ProductLibraryModalV39({
    /* data */
    senarai, terpilihIds, kuantiti, bolehPilihIds, bilPending,
    produkTerpilih, jumlahKuantiti,
    kiraSystem, kiraOwn, isTrial, tab,
    cari, kumpulanAktif, groupPilihan, namaGroup,
    susun, arah, papar, saizGambar = 's', memuat,
    /* tindakan */
    onTukarTab, onCari, onTukarGroup, onTukarSusun, onTukarPapar, onTukarSaiz,
    onPetik, onUbahKuantiti, onPilihSemua, onKosongkan, onHantar, onTutup,
    /* paparan setiap produk */
    idPapar,
}) {
    /* Esc menutup modal — jangkaan asas bagi apa-apa popup bertindih. */
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onTutup(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onTutup]);

    const semuaDitanda = bolehPilihIds.length > 0 && bolehPilihIds.every((id) => terpilihIds.has(id));

    return (
        <div
            className="s-root s-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Products"
            onClick={onTutup}
        >
            {/* 900 kotak Products + 10 jurang + 300 troli = 1210. Ketiga-tiga
                angka mesti bergerak bersama; menukar lebar troli tanpa
                menukar jumlah akan menyempitkan kotak Products.

                ⛔ Modal ini TIDAK penuh skrin. Percubaan melebarkannya
                (2026-08-15) ditolak user — 1210 kekal.

                TINGGI: 600px ialah LANTAI, bukan tinggi tetap. Kedua-dua panel
                mengambil tinggi baris flex yang sama (align-items: stretch),
                jadi hujung bawahnya sentiasa rata — dulu kotak Products dikunci
                600px sementara troli membesar ikut bilangan kad, dan bila
                pelanggan memilih banyak produk troli menjulur jauh ke bawah
                kotak Products. Siling 88vh dipegang di sini DAN pada kedua-dua
                panel; jangan buang mana-mana satu, kerana saiz baris dikira
                daripada tinggi kandungan panel (senarai 90 produk sendiri sudah
                melebihi skrin). */}
            <div style={{
                display: 'flex', alignItems: 'stretch', gap: 10,
                width: '100%', maxWidth: 1210,
                minHeight: 'min(600px, 88vh)', maxHeight: '88vh',
                flexWrap: 'wrap', justifyContent: 'center',
            }}>

                {/* ---------- Kotak Products ---------- */}
                {/* Tiada `height` di sini dengan sengaja: tinggi tetap akan
                    menarik diri keluar daripada align-items: stretch, dan itulah
                    yang dulu membuatkan kotak ini kekal 600px sementara troli
                    memanjang. Siling datang daripada `max-height: 88vh` dalam
                    .s-modal__panel; senarai di dalam kekal satu-satunya kawasan
                    yang menatal. */}
                <div
                    className="s-modal__panel"
                    style={{ maxWidth: 'none', flex: '1 1 480px', minWidth: 0 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <header className="s-modal__head" style={{ padding: '11px 14px' }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--s-ink)', flex: 'none' }}>
                            Products
                        </span>
                        <span className="s-dim" style={{ flex: 1, minWidth: 0 }}>{senarai.length}</span>
                        <button type="button" onClick={onTutup} aria-label="Close" title="Close (Esc)" className="s-modal__x">
                            <X size={14} strokeWidth={2} />
                        </button>
                    </header>

                    {/* SATU baris alat: tab · carian · group · susunan · paparan */}
                    <div style={{
                        flex: 'none', display: 'flex', alignItems: 'center', gap: 8,
                        padding: '9px 14px', borderBottom: '1px solid var(--s-line)', flexWrap: 'wrap',
                    }}>
                        {/* Tab My Products DIKUNCI untuk pengguna trial —
                            peraturan sedia ada modal lama, bukan hiasan. */}
                        <Segmented
                            value={tab}
                            onChange={onTukarTab}
                            options={[
                                { value: 'system', label: <>System <span style={{ color: 'var(--s-ink-3)' }}>{kiraSystem}</span></> },
                                {
                                    value: 'own',
                                    disabled: isTrial,
                                    label: isTrial
                                        ? <>My Products <Lock size={10} strokeWidth={2.2} style={{ verticalAlign: -1 }} /></>
                                        : <>My Products <span style={{ color: 'var(--s-ink-3)' }}>{kiraOwn}</span></>,
                                },
                            ]}
                        />

                        <span style={{ flex: 1, minWidth: 12 }} />

                        <Field
                            icon={<Search size={14} strokeWidth={1.9} />}
                            placeholder="Search"
                            value={cari}
                            onChange={(e) => onCari(e.target.value)}
                            style={{ flex: 'none', width: 190, minWidth: 0, height: 32, fontSize: 13 }}
                        />

                        {groupPilihan.length > 0 && (
                            <PilihV39
                                ariaLabel="Group"
                                lebar="auto"
                                tinggi={32}
                                saizTeks={12.5}
                                lebarMenu={190}
                                nilai={kumpulanAktif}
                                onPilih={onTukarGroup}
                                pilihan={[
                                    { id: 'all', label: 'All groups' },
                                    ...groupPilihan.map((g) => ({ id: g.id, label: g.name })),
                                ]}
                            />
                        )}

                        <SusunMenuV39 pilihan={SUSUNAN_V39} jenis={susun} arah={arah} onTukar={onTukarSusun} />

                        <div className="s-viewseg">
                            <button
                                type="button" aria-label="Grid view" title="Grid view"
                                aria-pressed={papar === 'grid'}
                                className={`s-viewseg__b${papar === 'grid' ? ' s-viewseg__b--on' : ''}`}
                                onClick={() => onTukarPapar('grid')}
                            >
                                <LayoutGrid size={14} strokeWidth={1.9} />
                            </button>
                            <button
                                type="button" aria-label="List view" title="List view"
                                aria-pressed={papar === 'jadual'}
                                className={`s-viewseg__b${papar === 'jadual' ? ' s-viewseg__b--on' : ''}`}
                                onClick={() => onTukarPapar('jadual')}
                            >
                                <Rows3 size={14} strokeWidth={1.9} />
                            </button>
                        </div>

                        {/* Saiz gambar — mod senarai SAHAJA (dalam grid, gambar
                            sudah sebesar kad). Pilihan yang SAMA dengan halaman
                            Products, jadi menukarnya di sini turut menukarnya di
                            sana — satu idea, satu tetapan. */}
                        {papar === 'jadual' && (
                            <div className="s-viewseg" role="group" aria-label="Image size">
                                {[
                                    { id: 's', teks: 'S', tip: 'Small images' },
                                    { id: 'm', teks: 'M', tip: 'Medium images' },
                                    { id: 'l', teks: 'L', tip: 'Large images' },
                                ].map((o) => (
                                    <button
                                        key={o.id}
                                        type="button"
                                        aria-label={o.tip} title={o.tip}
                                        aria-pressed={saizGambar === o.id}
                                        className={`s-viewseg__b${saizGambar === o.id ? ' s-viewseg__b--on' : ''}`}
                                        onClick={() => onTukarSaiz?.(o.id)}
                                        style={{ fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700 }}
                                    >
                                        {o.teks}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Jalur nipis: pilih semua · kunci PENDING */}
                    <div style={{
                        flex: 'none', display: 'flex', alignItems: 'center', gap: 9,
                        padding: '7px 14px', borderBottom: '1px solid var(--s-line)', flexWrap: 'wrap',
                    }}>
                        <Kotak on={semuaDitanda} mati={bolehPilihIds.length === 0} onChange={onPilihSemua} />
                        <span style={{ fontSize: 12, color: 'var(--s-ink-2)' }}>
                            Select all ({bolehPilihIds.length})
                        </span>
                        {bilPending > 0 && (
                            <span
                                title={`${bilPending} products can't be selected — their product data is incomplete. Complete them through Import Products in the Extension, then try again.`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--s-warn)' }}
                            >
                                <Info size={12} strokeWidth={2} />{bilPending} pending locked
                            </span>
                        )}
                    </div>

                    {/* Senarai — SATU-SATUNYA kawasan yang menatal */}
                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        {memuat ? (
                            <p className="s-hint" style={{ padding: '18px 14px' }}>Loading products…</p>
                        ) : senarai.length === 0 ? (
                            <p className="s-hint" style={{ padding: '18px 14px 22px' }}>
                                No products match. Clear the search or change the group.
                            </p>
                        ) : papar === 'grid' ? (
                            <div style={{
                                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(186px, 1fr))',
                                gap: 10, padding: 14,
                            }}>
                                {senarai.map((p) => {
                                    const kunci = !p.hasIntelligence;
                                    const on = terpilihIds.has(p.id);
                                    return (
                                        <div
                                            key={p.id}
                                            role="button"
                                            tabIndex={kunci ? -1 : 0}
                                            aria-pressed={on}
                                            onClick={() => !kunci && onPetik(p.id)}
                                            onKeyDown={(e) => {
                                                if (!kunci && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onPetik(p.id); }
                                            }}
                                            title={p.name}
                                            className={`s-card s-pcard${on ? ' s-pcard--on' : ''}`}
                                            style={{ cursor: kunci ? 'default' : 'pointer', opacity: kunci ? .5 : 1 }}
                                        >
                                            <div className="s-thumb s-thumb--besar" style={{ overflow: 'hidden' }}>
                                                {p.imageUrl
                                                    ? <img src={p.imageUrl} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    : <Package size={20} strokeWidth={1.4} />}

                                                <span style={{ position: 'absolute', top: 7, left: 7 }}>
                                                    <Kotak on={on} mati={kunci} onChange={() => onPetik(p.id)} />
                                                </span>
                                                <span style={{
                                                    position: 'absolute', top: 7, right: 7,
                                                    fontSize: 10, padding: '1px 6px', borderRadius: 5,
                                                    background: 'var(--s-ground)', border: '1px solid var(--s-line)',
                                                    color: p.hasIntelligence ? 'var(--s-ok)' : 'var(--s-warn)',
                                                }}>
                                                    {p.hasIntelligence ? 'Ready' : 'Pending'}
                                                </span>
                                            </div>

                                            <div style={{ padding: '8px 9px 10px' }}>
                                                <div className="s-num" style={{
                                                    fontSize: 10, lineHeight: 1.4,
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}>
                                                    #{idPapar(p)}
                                                </div>
                                                <div className="s-clamp2" style={{
                                                    fontSize: 12, lineHeight: 1.35, minHeight: 32, marginTop: 2, color: 'var(--s-ink)',
                                                }}>
                                                    {p.name}
                                                </div>
                                                {namaGroup(p) && (
                                                    <div style={{ display: 'flex', marginTop: 7, minWidth: 0 }}>
                                                        <span className="s-rowchip" style={{ minWidth: 0, maxWidth: '100%' }}>
                                                            <Tag size={10} strokeWidth={1.9} style={{ flex: 'none' }} />
                                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {namaGroup(p)}
                                                            </span>
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : senarai.map((p) => {
                            const kunci = !p.hasIntelligence;
                            const on = terpilihIds.has(p.id);
                            return (
                                <div
                                    key={p.id}
                                    onClick={() => !kunci && onPetik(p.id)}
                                    title={p.name}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 9,
                                        padding: '6px 14px', borderBottom: '1px solid var(--s-line)',
                                        cursor: kunci ? 'default' : 'pointer',
                                        background: on ? 'var(--s-fill)' : 'transparent',
                                    }}
                                >
                                    <Kotak on={on} mati={kunci} onChange={() => onPetik(p.id)} />

                                    <Gambar
                                        url={p.imageUrl}
                                        saiz={SAIZ_MODAL[saizGambar] || SAIZ_MODAL.s}
                                        radius={SAIZ_RADIUS[saizGambar] || SAIZ_RADIUS.s}
                                        ikon={SAIZ_IKON[saizGambar] || SAIZ_IKON.s}
                                    />

                                    {/* Lajur ID berlebar TETAP — itulah yang membuatkan
                                        ID setiap baris bermula pada titik yang sama.
                                        ⚠️ DISOROK PADA TELEFON (`sesaat-m-hide`). Lebarnya
                                        152px tetap, jadi pada skrin 390-500px ia melahap
                                        ruang dan nama produk tinggal SATU huruf ("B..",
                                        "Q..") — diukur 16px pada skrin 500px. Pelanggan
                                        mengenali produk melalui gambar + nama, bukan
                                        melalui nombor 19 digit, jadi nama yang menang. */}
                                    <span className="s-num sesaat-m-hide" style={{
                                        flex: 'none', width: 152, fontSize: 10.5, opacity: kunci ? .6 : 1,
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    }}>
                                        #{idPapar(p)}
                                    </span>

                                    <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 7 }}>
                                        <span style={{
                                            minWidth: 0, fontSize: 12.5,
                                            color: kunci ? 'var(--s-ink-3)' : 'var(--s-ink)',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}>
                                            {p.name}
                                        </span>
                                    </span>

                                    {/* Lajur group berlebar tetap, rata KIRI. Baris tanpa
                                        group mengekalkan sel kosong supaya lajur status
                                        di kanannya tidak berganjak.
                                        ⚠️ DISOROK PADA TELEFON atas sebab yang sama dengan
                                        lajur ID di atas — 120px lagi yang dirampas daripada
                                        nama produk. Group boleh ditapis melalui pemilih
                                        "All groups" di bar alat modal ini. */}
                                    <span className="sesaat-m-hide" style={{ flex: 'none', width: 120, display: 'flex', minWidth: 0 }}>
                                        {namaGroup(p) && (
                                            <span className="s-rowchip" style={{ fontSize: 10.5, minWidth: 0, maxWidth: '100%' }}>
                                                <Tag size={10} strokeWidth={1.9} style={{ flex: 'none' }} />
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {namaGroup(p)}
                                                </span>
                                            </span>
                                        )}
                                    </span>

                                    <span style={{
                                        flex: 'none', width: 52, textAlign: 'right', fontSize: 11,
                                        color: p.hasIntelligence ? 'var(--s-ok)' : 'var(--s-warn)',
                                    }}>
                                        {p.hasIntelligence ? 'Ready' : 'Pending'}
                                    </span>

                                    {/* Petak mangga SENTIASA ada, walaupun kosong — kalau
                                        tidak, baris berkunci sahaja yang berganjak dan
                                        lajur group nampak bengkok pada baris itu. */}
                                    <span style={{ flex: 'none', width: 13, display: 'grid', placeItems: 'center' }}>
                                        {kunci && <Lock size={12} strokeWidth={1.9} style={{ color: 'var(--s-ink-3)' }} />}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Tiada kaki di sini: Cancel + Add berpindah ke kaki troli
                        (arahan user 2026-08-15). Kiraan "N selected" tidak
                        dipulangkan sebagai baris kosong — kepala troli sudah
                        memapar "Selected (N)" yang sama. */}
                </div>

                {/* ---------- Troli · sentiasa terbuka ---------- */}
                <aside
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        flex: '0 0 300px', maxWidth: 300, minHeight: 0, maxHeight: '88vh',
                        display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        background: 'var(--s-panel)', border: '1px solid var(--s-line)',
                        borderRadius: 'var(--s-r-lg)',
                        boxShadow: '0 24px 64px -20px rgba(0, 0, 0, .7)',
                    }}
                >
                    <div style={{
                        flex: 'none', display: 'flex', alignItems: 'center', gap: 8,
                        padding: '13px 14px', borderBottom: '1px solid var(--s-line)',
                    }}>
                        <ShoppingCart size={14} strokeWidth={2} style={{ color: 'var(--s-ink-2)', flex: 'none' }} />
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: 'var(--s-ink)' }}>
                            Selected ({terpilihIds.size})
                        </span>
                        {terpilihIds.size > 0 && (
                            <button
                                type="button" onClick={onKosongkan} title="Clear all"
                                style={{
                                    flex: 'none', padding: 0, border: 0, background: 'none', cursor: 'pointer',
                                    fontFamily: 'inherit', fontSize: 11.5, color: 'var(--s-ink-3)',
                                }}
                            >
                                Clear
                            </button>
                        )}
                    </div>

                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {produkTerpilih.length === 0 && (
                            <p className="s-hint" style={{ margin: '6px 4px 0', textAlign: 'center', lineHeight: 1.5 }}>
                                Tick products on the left.<br />Card count is set here.
                            </p>
                        )}
                        {produkTerpilih.map((p) => (
                            <div
                                key={p.id}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8, padding: 7,
                                    background: 'var(--s-card)', border: '1px solid var(--s-line)',
                                    borderRadius: 10,
                                }}
                            >
                                <Gambar url={p.imageUrl} saiz={30} radius={7} ikon={13} />

                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span className="s-num" style={{
                                        display: 'block', fontSize: 11, lineHeight: 1.3,
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                        #{idPapar(p)}
                                    </span>
                                    <span style={{
                                        display: 'block', fontSize: 12, color: 'var(--s-ink)',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }} title={p.name}>{p.name}</span>
                                </span>

                                <Pelaras nilai={kuantiti[p.id] || 1} onUbah={(d) => onUbahKuantiti(p.id, d)} />

                                <button
                                    type="button"
                                    onClick={() => onPetik(p.id)}
                                    title="Remove" aria-label="Remove from selection"
                                    style={{
                                        width: 20, height: 20, flex: 'none', padding: 0, borderRadius: 999,
                                        display: 'grid', placeItems: 'center', cursor: 'pointer',
                                        background: 'none', border: 0, color: 'var(--s-ink-3)',
                                    }}
                                >
                                    <X size={12} strokeWidth={2.2} />
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Kaki troli — jumlah kad + dua butang tindakan.
                        Butang bertindak ke atas apa yang ada dalam troli, jadi
                        di sinilah tempatnya; di kaki kotak Products dahulu ia
                        duduk jauh di seberang nombor yang ia sahkan.
                        Butang disusun MENURUN (bukan sebaris dengan nombor):
                        troli hanya 300px, dan "Add (128)" bersebelahan label
                        TOTAL CARD sudah cukup untuk memaksa baris itu pecah. */}
                    <div style={{
                        flex: 'none', padding: '11px 14px', borderTop: '1px solid var(--s-line)',
                        background: 'var(--s-card-foot)',
                    }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', color: 'var(--s-ink-3)' }}>
                            TOTAL CARD
                        </div>
                        <div style={{
                            fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                            color: jumlahKuantiti > 0 ? 'var(--s-ink)' : 'var(--s-ink-3)',
                        }}>
                            {jumlahKuantiti}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                            <Btn variant="ghost" size="sm" onClick={onTutup} style={{ flex: 'none' }}>
                                Cancel
                            </Btn>
                            <Btn
                                size="sm"
                                disabled={terpilihIds.size === 0}
                                icon={<ShoppingCart size={13} strokeWidth={2} />}
                                onClick={onHantar}
                                style={{ flex: 1, minWidth: 0 }}
                            >
                                Add ({jumlahKuantiti})
                            </Btn>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}
